import type { QuoteLine } from '../../lib/sales-pricing';
import {
  calendarDateUtc,
  editionIncludedFlows,
  isLegacySeatEdition,
  type PricingProposal,
  type ProposalAddonSnapshot,
  type ProposalCatalogSnapshot,
  type ProposalEditionSnapshot,
} from './proposal-types';

export function money(minor: number, currency: string, precise = false): string {
  if (typeof minor !== 'number' || !Number.isFinite(minor)) return '—';
  const needsCents = precise || Math.abs(minor) % 100 !== 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: needsCents ? 2 : 0,
    maximumFractionDigits: needsCents ? 2 : 0,
  }).format(minor / 100);
}

const MONTHS_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatUtcLongDate(iso: string): string {
  const ymd = calendarDateUtc(iso);
  if (!ymd) return iso;
  const [y, m, d] = ymd.split('-').map(Number);
  return `${MONTHS_EN[m - 1]} ${d}, ${y}`;
}

/** Timestamp / date-only → “October 16, 2026” from UTC calendar parts (no local day shift). */
export function formatLongDate(iso: string): string {
  return formatUtcLongDate(iso);
}

/** Proposal validity is a calendar day — date-only or ISO, always UTC Y/M/D. */
export function formatValidUntil(iso: string): string {
  return formatUtcLongDate(iso);
}

export function formatMonthYear(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
}

export function currencyLabel(code: string): string {
  return (code || 'USD').toUpperCase();
}

function isVolumeInclude(s: string): boolean {
  return /\d[\d,]*\s+flows?\b/i.test(s);
}

/** Expand “Everything in Team” so later editions inherit earlier include lists. */
export function expandedIncludes(editions: ProposalEditionSnapshot[]): Map<string, string[]> {
  const byName = new Map(editions.map((e) => [e.name.toLowerCase(), e]));
  const out = new Map<string, string[]>();
  for (const ed of editions) {
    const items: string[] = [];
    const ownVolume = ed.included.some(isVolumeInclude);
    for (const raw of ed.included) {
      const inherit = /^everything in (.+)$/i.exec(raw.trim());
      if (inherit) {
        const parent = byName.get(inherit[1].toLowerCase());
        if (parent) {
          for (const p of out.get(parent.id) ?? parent.included) {
            if (/^everything in /i.test(p)) continue;
            // A later edition’s own include volume replaces the parent’s.
            if (ownVolume && isVolumeInclude(p)) continue;
            items.push(p);
          }
        }
        continue;
      }
      items.push(raw);
    }
    out.set(ed.id, unique(items));
  }
  return out;
}

function unique(xs: string[]): string[] {
  return [...new Set(xs)];
}

export function capabilityMatrix(editions: ProposalEditionSnapshot[]): Array<{
  label: string;
  flags: boolean[];
}> {
  const expanded = expandedIncludes(editions);
  const labels: string[] = [];
  for (const ed of editions) {
    for (const item of expanded.get(ed.id) ?? []) {
      if (!labels.includes(item)) labels.push(item);
    }
  }
  return labels.map((label) => ({
    label,
    flags: editions.map((ed) => (expanded.get(ed.id) ?? []).includes(label)),
  }));
}

export function commonIncludes(editions: ProposalEditionSnapshot[]): string[] {
  if (editions.length === 0) return [];
  const expanded = expandedIncludes(editions);
  const first = expanded.get(editions[0].id) ?? [];
  return first.filter((item) => editions.every((ed) => (expanded.get(ed.id) ?? []).includes(item)));
}

export function addonsOf(
  catalog: ProposalCatalogSnapshot | undefined,
  category: ProposalAddonSnapshot['category'],
): ProposalAddonSnapshot[] {
  return (catalog?.addons ?? []).filter((a) => a.category === category);
}

/** Plan-card amount: platform list, or the stored per-seat list — never invented. */
export function editionPlanPriceMinor(ed: ProposalEditionSnapshot): number | null {
  if (isLegacySeatEdition(ed)) {
    return Number.isFinite(ed.startingPerSeatAnnualMinor) ? ed.startingPerSeatAnnualMinor! : null;
  }
  return Number.isFinite(ed.listAnnualMinor) ? ed.listAnnualMinor! : null;
}

export function editionPlanSubtitle(
  ed: ProposalEditionSnapshot,
  cur: string,
  quoteUnlimitedSeats = false,
): string {
  if (isLegacySeatEdition(ed)) {
    const min = ed.minSeats;
    const seats = typeof min === 'number' ? `${min.toLocaleString()}+ named seats` : 'named seats as quoted';
    return `${cur} per seat / year · ${seats}`;
  }
  return quoteUnlimitedSeats && ed.unlimitedSeats === true
    ? `${cur} per year · unlimited seats`
    : `${cur} per year`;
}

export function editionCapacityLine(ed: ProposalEditionSnapshot, currency: string): string {
  if (isLegacySeatEdition(ed)) {
    const flows = editionIncludedFlows(ed);
    const flowPart =
      typeof flows === 'number' ? `${flows.toLocaleString()} flows / yr` : 'Included flows as quoted';
    return `${flowPart} · published AI packs`;
  }
  const flows =
    ed.includedFlowsAnnual == null
      ? 'Custom flows'
      : `${ed.includedFlowsAnnual.toLocaleString()} flows / yr`;
  const ai =
    ed.includedAiSpendAnnualMinor == null
      ? 'Custom AI spend'
      : `${money(ed.includedAiSpendAnnualMinor, currency)} AI spend`;
  return `${flows} · ${ai}`;
}

export function editionForQuote(
  proposal: PricingProposal,
): ProposalEditionSnapshot | undefined {
  const id = proposal.quote.input.editionId;
  return proposal.catalog?.editions.find((e) => e.id === id);
}

/**
 * Catalog overview / add-on slides may render only when the snapshot matches
 * the stored quote: the quoted edition is present and the edition schema
 * agrees with the quote (named-seat vs platform-fee). A 2026.09.2+
 * Team/Scale/Enterprise snapshot must not drive slides for a 2026.09.1
 * Growth named-seat quote.
 */
export function catalogMatchesQuotedSchema(proposal: PricingProposal): boolean {
  const catalog = proposal.catalog;
  if (!catalog || catalog.editions.length === 0) return false;
  const selected = editionForQuote(proposal);
  if (!selected) return false;
  const quoteIsNew = proposal.quote.unlimitedSeats === true;
  const editionIsLegacy = isLegacySeatEdition(selected);
  if (quoteIsNew) return !editionIsLegacy && (selected.listAnnualMinor ?? 0) > 0;
  return editionIsLegacy;
}

export function selectedIncludes(proposal: PricingProposal): string[] {
  const ed = editionForQuote(proposal);
  if (!ed || !proposal.catalog) return [];
  return expandedIncludes(proposal.catalog.editions).get(ed.id) ?? ed.included;
}

export function platformLine(quote: PricingProposal['quote']): QuoteLine | undefined {
  return quote.lines.find((l) => l.category === 'platform');
}

export function lineOf(
  quote: PricingProposal['quote'],
  category: QuoteLine['category'],
): QuoteLine | undefined {
  return quote.lines.find((l) => l.category === category);
}

export function annualize(line: QuoteLine | undefined, termYears: number): number | null {
  if (!line || line.billing === 'one_time') return null;
  if (!termYears) return line.netMinor;
  return Math.round(line.netMinor / termYears);
}

export function isPilotLine(line: QuoteLine): boolean {
  return /pilot/i.test(line.skuId) || /pilot/i.test(line.name);
}

/** True only when the quote actually bills implementation or a pilot — never by edition alone. */
export function showEnterpriseServices(proposal: PricingProposal): boolean {
  return quotedServiceLines(proposal).length > 0;
}

/** Every quoted implementation or pilot line — pilot does not hide implementation. */
export function quotedServiceLines(proposal: PricingProposal): QuoteLine[] {
  return proposal.quote.lines.filter((l) => l.category === 'implementation' || isPilotLine(l));
}

export interface InvestmentRow {
  skuId?: string;
  item: string;
  detail: string;
  annualMinor: number | null;
  oneTimeMinor: number | null;
  included: boolean;
}

function recurringRow(
  item: string,
  detail: string,
  annualMinor: number | null,
  skuId?: string,
): InvestmentRow {
  return { skuId, item, detail, annualMinor, oneTimeMinor: null, included: false };
}

function includedRow(item: string, detail: string): InvestmentRow {
  return { item, detail, annualMinor: null, oneTimeMinor: null, included: true };
}

function oneTimeRow(item: string, detail: string, oneTimeMinor: number, skuId?: string): InvestmentRow {
  return { skuId, item, detail, annualMinor: null, oneTimeMinor, included: false };
}

function lineItemLabel(line: QuoteLine): string {
  if (isPilotLine(line)) return 'AI Transformation Pilot';
  switch (line.category) {
    case 'platform':
      return 'Platform fee';
    case 'ai_credits':
      return 'AI capacity packs';
    case 'usage':
      return 'Additional flows';
    case 'overage':
      return 'Additional flows';
    case 'portal':
      return 'Portal add-on';
    case 'implementation':
      return 'Implementation';
    default:
      return line.name;
  }
}

/** True only when the stored quote itself opts into unlimited seats. */
export function quoteHasUnlimitedSeats(proposal: PricingProposal): boolean {
  return proposal.quote.unlimitedSeats === true;
}

/** AI-job billing copy requires the new-schema quote flag and a stored aiJobs payload. */
export function proposalHasAiJobBilling(proposal: PricingProposal): boolean {
  return quoteHasUnlimitedSeats(proposal) && proposal.catalog?.aiJobs != null;
}

/** Legacy named-seat count from the stored quote — never inferred from today's catalog. */
export function quotedNamedSeatCount(quote: PricingProposal['quote']): number | null {
  const fromInput = quote.input.seats;
  if (typeof fromInput === 'number' && fromInput > 0) return fromInput;
  const platform = quote.lines.find((l) => l.category === 'platform');
  if (platform && platform.quantity > 1) return platform.quantity;
  return null;
}

/** Footer / line copy: unlimited only with an explicit new-schema signal. */
export function seatTermsLabel(proposal: PricingProposal): string {
  if (quoteHasUnlimitedSeats(proposal)) return 'unlimited seats';
  const n = quotedNamedSeatCount(proposal.quote);
  if (n != null) return `${n.toLocaleString()} named seats`;
  const note = proposal.quote.lines.find((l) => l.category === 'platform')?.note;
  if (note && /seat/i.test(note)) return note;
  if (proposal.quote.seatTierId) return `named seats (${proposal.quote.seatTierId})`;
  return 'named seats as quoted';
}

function lineItemDetail(line: QuoteLine, quote: PricingProposal['quote'], proposal: PricingProposal): string {
  if (isPilotLine(line)) {
    return line.note || (line.billing === 'one_time' ? `${line.name} · one-time` : line.name);
  }
  switch (line.category) {
    case 'platform':
      return `Moxo ${quote.editionName} plan · ${seatTermsLabel(proposal)}`;
    case 'overage':
      return `${line.quantity.toLocaleString()} additional flows`;
    case 'portal':
      return line.note || line.name;
    case 'implementation':
      return line.billing === 'one_time' ? `${line.name} · one-time` : line.name;
    default:
      return line.name;
  }
}

function rowFromLine(line: QuoteLine, proposal: PricingProposal, years: number): InvestmentRow {
  const item = lineItemLabel(line);
  const detail = lineItemDetail(line, proposal.quote, proposal);
  if (line.billing === 'one_time') {
    return oneTimeRow(item, detail, line.netMinor, line.skuId);
  }
  return recurringRow(item, detail, annualize(line, years), line.skuId);
}

export function investmentRowAnnualTotal(rows: InvestmentRow[]): number {
  return rows.reduce((s, r) => s + (r.annualMinor ?? 0), 0);
}

export function investmentRowOneTimeTotal(rows: InvestmentRow[]): number {
  return rows.reduce((s, r) => s + (r.oneTimeMinor ?? 0), 0);
}

/** Per-line annualize rounding can drift from quote.arrNetMinor — pin the first billed row. */
function reconcileRowTotals(rows: InvestmentRow[], quote: PricingProposal['quote']): InvestmentRow[] {
  const annualDelta = quote.arrNetMinor - investmentRowAnnualTotal(rows);
  if (annualDelta !== 0) {
    const target = rows.find((r) => r.annualMinor != null);
    if (target) target.annualMinor = (target.annualMinor ?? 0) + annualDelta;
  }
  const oneTimeDelta = quote.oneTimeNetMinor - investmentRowOneTimeTotal(rows);
  if (oneTimeDelta !== 0) {
    const target = rows.find((r) => r.oneTimeMinor != null);
    if (target) target.oneTimeMinor = (target.oneTimeMinor ?? 0) + oneTimeDelta;
  }
  return rows;
}

export function investmentRows(proposal: PricingProposal): InvestmentRow[] {
  const { quote } = proposal;
  const years = quote.termYears || 1;
  const ed = editionForQuote(proposal);
  const billed = quote.lines.map((line) => rowFromLine(line, proposal, years));
  const platformRows = billed.filter((r) => r.item === 'Platform fee');
  const rest = billed.filter((r) => r.item !== 'Platform fee');

  const rows: InvestmentRow[] = [];
  if (platformRows.length > 0) {
    rows.push(...platformRows);
  } else {
    rows.push(recurringRow('Platform fee', `${quote.editionName} plan`, null));
  }
  const includedFlows = editionIncludedFlows(ed);
  rows.push(
    includedRow(
      'Included flows',
      ed
        ? includedFlows == null && !isLegacySeatEdition(ed)
          ? 'Custom flows per year'
          : typeof includedFlows === 'number'
            ? `${includedFlows.toLocaleString()} ${includedFlows === 1 ? 'flow' : 'flows'} per year`
            : 'Edition include'
        : 'Edition include',
    ),
  );
  if (!quote.lines.some((l) => l.category === 'ai_credits')) {
    const ai =
      ed && isLegacySeatEdition(ed)
        ? 'Published AI packs'
        : ed?.includedAiSpendAnnualMinor != null
          ? `${money(ed.includedAiSpendAnnualMinor, quote.currency)} per year`
          : ed
            ? 'Custom AI spend per year'
            : 'Included AI spend on the published edition';
    rows.push(includedRow('Included AI spend', ai));
  }
  rows.push(...rest);
  return reconcileRowTotals(rows, quote);
}

export interface InvestmentSummary {
  rows: InvestmentRow[];
  annualRecurringMinor: number;
  oneTimeMinor: number;
  termYears: number;
  termMonths: number;
  termListMinor: number;
  termDiscountMinor: number;
  termNetMinor: number;
  unlimitedSeats: boolean;
  seatTerms: string;
  /** Proven only from the stored quote + snapshot — never inferred for legacy /r. */
  aiJobsAtCost: boolean;
}

export function investmentSummary(proposal: PricingProposal): InvestmentSummary {
  const rows = investmentRows(proposal);
  const { quote } = proposal;
  return {
    rows,
    annualRecurringMinor: quote.arrNetMinor,
    oneTimeMinor: quote.oneTimeNetMinor,
    termYears: quote.termYears || 1,
    termMonths: quote.input.termMonths,
    termListMinor: quote.listMinor,
    termDiscountMinor: quote.discountMinor,
    termNetMinor: quote.netMinor,
    unlimitedSeats: quoteHasUnlimitedSeats(proposal),
    seatTerms: seatTermsLabel(proposal),
    aiJobsAtCost: proposalHasAiJobBilling(proposal),
  };
}

export function formatInvestmentCell(row: InvestmentRow, currency: string): string {
  if (row.oneTimeMinor != null) return `${money(row.oneTimeMinor, currency)} one-time`;
  if (row.included && row.annualMinor == null) return 'Included';
  if (row.annualMinor == null) return '—';
  return money(row.annualMinor, currency);
}

/** @deprecated use formatInvestmentCell */
export function formatAnnualCell(row: InvestmentRow, currency: string): string {
  return formatInvestmentCell(row, currency);
}

export function investmentFinePrint(summary: InvestmentSummary, currency: string, cur: string): string {
  const annual = money(summary.annualRecurringMinor, currency);
  const termNet = money(summary.termNetMinor, currency);
  const parts = [
    `All prices in ${cur} · ${summary.unlimitedSeats ? 'no per seat fees' : summary.seatTerms} · ${summary.aiJobsAtCost ? 'AI jobs billed at cost' : 'published AI packs'}`,
    `Annual recurring net ${annual}`,
  ];
  if (summary.oneTimeMinor > 0) {
    parts.push(`one-time services ${money(summary.oneTimeMinor, currency)}`);
  }
  parts.push(`${summary.termMonths}-month term net ${termNet}`);
  if (summary.termDiscountMinor > 0) {
    parts.push(
      `term list ${money(summary.termListMinor, currency)} − discount ${money(summary.termDiscountMinor, currency)} = net ${termNet}`,
    );
  }
  return `${parts.join(' · ')}.`;
}

/** Prefer Fedja plan columns (Workflows / AI + agents / Experience + ops). */
export function planFeatureColumns(
  ed: ProposalEditionSnapshot | undefined,
  fallback: string[] = [],
): Array<{ title: string; items: string[] }> {
  if (ed?.featureGroups) {
    return [
      { title: 'Workflows', items: ed.featureGroups.workflows },
      { title: 'AI + agents', items: ed.featureGroups.aiAgents },
      { title: 'Experience + ops', items: ed.featureGroups.experienceOps },
    ].filter((c) => c.items.length > 0);
  }
  return featureColumns(fallback);
}

/** Split include bullets into three presentation columns (Fedja layout). */
export function featureColumns(items: string[]): Array<{ title: string; items: string[] }> {
  if (items.length === 0) {
    return [
      { title: 'Platform', items: ['Published edition features apply'] },
    ];
  }
  const titles = ['Workflows', 'AI + agents', 'Experience + ops'];
  if (items.length <= 3) {
    return [{ title: 'Included', items }];
  }
  const cols: string[][] = [[], [], []];
  items.forEach((item, i) => {
    cols[i % 3].push(item);
  });
  return titles.map((title, i) => ({ title, items: cols[i] })).filter((c) => c.items.length > 0);
}

export function perFlowMinor(addon: ProposalAddonSnapshot): number | null {
  if (!addon.prepaidRuns || !addon.listAnnualMinor) return null;
  return addon.listAnnualMinor / addon.prepaidRuns;
}

